import { useEffect, useRef } from 'react';

import { Input } from '../../../primitives/input';
import { InputNumber } from '../../../primitives/input-number';
import { cn } from '../../../utils/cn';
import { type BucketInValue } from '../../input/input-bucket-in';
import { type VariantSchemaValue } from '../../input/input-variant-schema';
import { MultiSelector, type Option } from '../../multi-selector/multi-selector';
import { SwitchCard } from '../../switch/switch-card';
import { type TFact } from '../types';
import { isDateMathMissingBaseDate } from '../utils';
import { InputBucketIn } from './input-bucket-in';
import { InputDate } from './input-date';
import { InputVariantSchema } from './input-variant-schema';

// ─── Value case determination ─────────────────────────────────────────────────

type ValueCase =
  | 'NO_INPUT'
  | 'INPUT'
  | 'STRING_EQUAL'
  | 'MULTI_SELECTOR_ARRAY'
  | 'MULTI_SELECTOR_SINGLE'
  | 'INPUT_NUMBER'
  | 'SWITCH'
  | 'INPUT_DATE'
  | 'BUCKET_IN'
  | 'VARIANT_SCHEMA';

export function getValueCase(codeDataType: string, operator: string): ValueCase {
  if (
    operator === 'isEmpty' ||
    operator === 'notEmpty' ||
    operator === 'isEven' ||
    operator === 'isOdd'
  ) {
    return 'NO_INPUT';
  }

  if (operator === 'segment') return 'BUCKET_IN';
  if (operator === 'variantSchema') return 'VARIANT_SCHEMA';

  if (codeDataType === 'string') {
    if (operator === 'equal' || operator === 'notEqual') return 'STRING_EQUAL';
    if (operator === 'startsWith' || operator === 'endsWith') return 'INPUT';
    if (operator === 'in' || operator === 'notIn') return 'MULTI_SELECTOR_ARRAY';
  }

  if (codeDataType === 'number') {
    if (
      operator === 'equal' ||
      operator === 'notEqual' ||
      operator === 'lessThan' ||
      operator === 'lessThanInclusive' ||
      operator === 'greaterThan' ||
      operator === 'greaterThanInclusive' ||
      operator === 'startsWith' ||
      operator === 'endsWith'
    ) {
      return 'INPUT_NUMBER';
    }
  }

  if (codeDataType === 'boolean') {
    if (operator === 'equal' || operator === 'notEqual') return 'SWITCH';
  }

  if (codeDataType === 'array') {
    if (operator === 'contains' || operator === 'doesNotContain') return 'MULTI_SELECTOR_SINGLE';
  }

  if (codeDataType === 'date') {
    if (
      operator === 'equal' ||
      operator === 'notEqual' ||
      operator === 'lessThan' ||
      operator === 'lessThanInclusive' ||
      operator === 'greaterThan' ||
      operator === 'greaterThanInclusive'
    ) {
      return 'INPUT_DATE';
    }
  }

  return 'INPUT';
}

function getDefaultValue(valueCase: ValueCase): unknown {
  if (valueCase === 'NO_INPUT' || valueCase === 'SWITCH') return true;
  if (valueCase === 'MULTI_SELECTOR_ARRAY') return [];
  if (valueCase === 'INPUT_NUMBER') return undefined;
  if (valueCase === 'BUCKET_IN') return { ranges: [], lockedQuadrants: [] };
  if (valueCase === 'VARIANT_SCHEMA') return { ranges: [] };
  return '';
}

export function isValueCompatible(value: unknown, valueCase: ValueCase): boolean {
  switch (valueCase) {
    case 'NO_INPUT':
      return value === true;
    case 'SWITCH':
      return typeof value === 'boolean';
    case 'INPUT':
    case 'STRING_EQUAL':
    case 'MULTI_SELECTOR_SINGLE':
      return typeof value === 'string';
    case 'MULTI_SELECTOR_ARRAY':
      return Array.isArray(value) && value.every((v) => typeof v === 'string');
    case 'INPUT_NUMBER':
      return typeof value === 'number';
    case 'INPUT_DATE':
      return (
        (typeof value === 'number' && Number.isFinite(value)) ||
        (typeof value === 'string' &&
          value.includes('dateMath') &&
          !isDateMathMissingBaseDate(value))
      );
    case 'BUCKET_IN':
    case 'VARIANT_SCHEMA':
      return typeof value === 'object' && value !== null && !Array.isArray(value);
  }
}

/** Returns true when a condition value should be considered empty / not yet filled. */
function isBucketInValueValid(value: unknown): boolean {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v['saltBase'] === 'string' &&
    v['saltBase'].trim() !== '' &&
    Array.isArray(v['ranges']) &&
    (v['ranges'] as unknown[]).length > 0
  );
}

export function isValueEmpty(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === 'object' && !Array.isArray(value) && 'ranges' in value) {
    return !isBucketInValueValid(value);
  }
  return false;
}

function inferCodeDataTypeFromOperator(operator: string, value: unknown): string {
  if (operator === 'contains' || operator === 'doesNotContain') return 'array';
  if (operator === 'in' || operator === 'notIn') return 'string';
  if (operator === 'isEven' || operator === 'isOdd') return 'number';

  if (operator === 'equal' || operator === 'notEqual') {
    if (typeof value === 'boolean') return 'boolean';
    if (typeof value === 'number') return 'number';
    if (typeof value === 'string' && value.includes('dateMath')) return 'date';
  }

  if (
    operator === 'lessThan' ||
    operator === 'lessThanInclusive' ||
    operator === 'greaterThan' ||
    operator === 'greaterThanInclusive'
  ) {
    if (typeof value === 'string' && value.includes('dateMath')) return 'date';
    if (typeof value === 'number') return 'number';
  }

  return '';
}

function getTextValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value === undefined || value === null) return '';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

interface ValueInputProps {
  disabled?: boolean;
  fact: string;
  factType?: string;
  facts: TFact[];
  id?: string;
  isValueMissing?: boolean;
  operator: string;
  pathType?: string;
  readonly?: boolean;
  value: unknown;
  onChange: (value: unknown) => void;
  onFactChange?: (fact: string) => void;
}

export function ValueInput({
  disabled,
  fact,
  factType,
  facts,
  id,
  isValueMissing = false,
  operator,
  pathType,
  readonly = false,
  value,
  onChange,
  onFactChange,
}: ValueInputProps) {
  const selectedFact = facts.find((f) => f.attributeName === fact);
  const effectiveFactType = (factType ?? selectedFact?.codeDataType ?? '').toLowerCase();
  const isFactObsolete = !selectedFact && fact.trim() !== '';
  const inferredCodeDataType = isFactObsolete ? inferCodeDataTypeFromOperator(operator, value) : '';
  const codeDataType =
    effectiveFactType === 'object'
      ? (pathType ?? '').toLowerCase().trim()
      : effectiveFactType || inferredCodeDataType;
  const valueCase = getValueCase(codeDataType, operator);

  // Use refs so the effect reads latest values without needing them as deps.
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // When fact/operator changes, reset value if its type is no longer compatible.
  useEffect(() => {
    if (isFactObsolete) return;
    if (!isValueCompatible(valueRef.current, valueCase)) {
      onChangeRef.current(getDefaultValue(valueCase));
    }
  }, [fact, isFactObsolete, operator, valueCase]);

  // When fact changes while in BUCKET_IN case, sync saltBase to the new fact name.
  useEffect(() => {
    if ((valueCase !== 'BUCKET_IN' && valueCase !== 'VARIANT_SCHEMA') || !fact) return;
    const current =
      typeof valueRef.current === 'object' &&
      valueRef.current !== null &&
      !Array.isArray(valueRef.current) &&
      'ranges' in valueRef.current
        ? (valueRef.current as BucketInValue | VariantSchemaValue)
        : { ranges: [] };
    if (current.saltBase !== fact) {
      onChangeRef.current({ ...current, saltBase: fact });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fact]);

  if (valueCase === 'NO_INPUT') return null;

  if (valueCase === 'SWITCH') {
    const checked = typeof value === 'boolean' ? value : true;
    return (
      <SwitchCard
        checked={checked}
        disabled={disabled}
        id={id}
        label={checked ? 'Verdadero' : 'Falso'}
        sizesx="sm"
        onCheckedChange={onChange}
      />
    );
  }

  if (valueCase === 'INPUT_NUMBER') {
    return (
      <InputNumber
        className={cn({ 'border-destructive': isValueMissing })}
        decimalScale={10}
        disabled={disabled}
        id={id}
        min={operator === 'endsWith' ? 0 : undefined}
        name={id}
        value={typeof value === 'number' ? value : undefined}
        onValueChange={onChange}
      />
    );
  }

  if (valueCase === 'INPUT_DATE') {
    return (
      <InputDate
        dateFactsModel={facts.filter((f) => f.codeDataType?.toLowerCase() === 'date')}
        disabled={disabled}
        isInvalid={isValueMissing}
        value={typeof value === 'number' || typeof value === 'string' ? value : ''}
        onChange={({ value: v }) => onChange(v)}
      />
    );
  }

  if (valueCase === 'BUCKET_IN') {
    const bucketValue: BucketInValue =
      typeof value === 'object' && value !== null && !Array.isArray(value) && 'ranges' in value
        ? (value as BucketInValue)
        : { ranges: [], lockedQuadrants: [] };

    const bucketInFactOptions = facts
      .filter((f) => {
        const codeDataType = f.codeDataType?.toLowerCase();
        return codeDataType === 'string' || codeDataType === 'number';
      })
      .map((f) => ({
        attributeName: f.attributeName,
        codeDataType: f.codeDataType,
      }));

    return (
      <InputBucketIn
        disabled={disabled}
        readonly={readonly}
        saltBaseOptions={bucketInFactOptions}
        value={{ ...bucketValue, saltBase: fact }}
        onChange={(v) => onChange({ ...v, saltBase: fact })}
        onSaltBaseChange={(selectedFact) => {
          onFactChange?.(selectedFact);
          onChange({ ...bucketValue, saltBase: selectedFact });
        }}
      />
    );
  }

  if (valueCase === 'VARIANT_SCHEMA') {
    const schemaValue: VariantSchemaValue =
      typeof value === 'object' && value !== null && !Array.isArray(value) && 'ranges' in value
        ? (value as VariantSchemaValue)
        : { ranges: [], saltBase: fact };

    const variantSchemaFactOptions = facts
      .filter((f) => {
        const codeDataType = f.codeDataType?.toLowerCase();
        return codeDataType === 'string' || codeDataType === 'number';
      })
      .map((f) => ({
        attributeName: f.attributeName,
        codeDataType: f.codeDataType,
      }));

    return (
      <InputVariantSchema
        disabled={disabled}
        value={{ ...schemaValue, saltBase: fact }}
        onChange={(v) => onChange({ ...v, saltBase: fact })}
        saltBaseOptions={variantSchemaFactOptions}
        onSaltBaseChange={(selectedFact) => {
          onFactChange?.(selectedFact);
          onChange({ ...schemaValue, saltBase: selectedFact });
        }}
      />
    );
  }

  if (valueCase === 'STRING_EQUAL') {
    const allowedValues = selectedFact?.allowedValues ?? [];
    if (allowedValues.length > 0) {
      const allowedOptions: Option[] = allowedValues.map((v) => ({ value: v, label: v }));
      const selectedOptions: Option[] =
        typeof value === 'string' && value ? [{ value, label: value }] : [];
      return (
        <div className="space-y-1">
          <p className="px-3 text-xs text-muted-foreground">Máximo 1 opción</p>
          <MultiSelector
            className={cn({ 'border-destructive': isValueMissing })}
            creatable
            inputProps={{ name: id }}
            options={allowedOptions}
            disabled={disabled}
            maxSelected={1}
            placeholder={
              selectedOptions.length === 0
                ? 'Escribe aquí para buscar o crear una opción'
                : undefined
            }
            value={selectedOptions}
            onChange={(opts) => onChange(opts[0]?.value ?? '')}
          />
        </div>
      );
    }
    // No allowedValues → plain text input
    return (
      <Input
        className={cn({ 'border-destructive': isValueMissing })}
        disabled={disabled}
        id={id}
        name={id}
        placeholder="Escribe aquí para ingresar un valor"
        sizesx="sm"
        value={getTextValue(value)}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (valueCase === 'MULTI_SELECTOR_ARRAY') {
    const allowedValues = selectedFact?.allowedValues ?? [];
    const allowedOptions: Option[] | undefined =
      allowedValues.length > 0 ? allowedValues.map((v) => ({ value: v, label: v })) : undefined;
    const selectedOptions: Option[] = Array.isArray(value)
      ? (value as string[]).map((v) => ({ value: v, label: v }))
      : [];
    return (
      <div className="space-y-1">
        <p className="px-3 text-xs text-muted-foreground">Selección múltiple</p>
        <MultiSelector
          className={cn({ 'border-destructive': isValueMissing })}
          creatable
          inputProps={{ name: id }}
          options={allowedOptions}
          disabled={disabled}
          placeholder="Escribe aquí para buscar o crear una opción"
          value={selectedOptions}
          onChange={(opts) => onChange(opts.map((o) => o.value))}
        />
      </div>
    );
  }

  if (valueCase === 'MULTI_SELECTOR_SINGLE') {
    const allowedOptions: Option[] | undefined =
      fact === 'suggested_investment_product_item'
        ? [{ value: '{{ id }}', label: '{{ id }}' }]
        : undefined;
    const selectedOptions: Option[] =
      typeof value === 'string' && value ? [{ value, label: value }] : [];
    return (
      <div className="space-y-1">
        <p className="px-3 text-xs text-muted-foreground">Máximo 1 opción</p>
        <MultiSelector
          className={cn({ 'border-destructive': isValueMissing })}
          creatable
          inputProps={{ name: id }}
          options={allowedOptions}
          disabled={disabled}
          maxSelected={1}
          placeholder="Valor"
          value={selectedOptions}
          onChange={(opts) => onChange(opts[0]?.value ?? '')}
        />
      </div>
    );
  }

  // Fallback: plain text input
  return (
    <Input
      className={cn({ 'border-destructive': isValueMissing })}
      disabled={disabled}
      id={id}
      name={id}
      placeholder="Escribe aquí para ingresar un valor"
      sizesx="sm"
      value={getTextValue(value)}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}
